import { EmployeeAvatar, useCircleSize } from "arkh";
import { Avatar_employee$key } from "arkh/src/cross-platform/Actor/__generated__/Avatar_employee.graphql";
import { View } from "react-native";

type OverlappingEmployee = {
  readonly id: string;
  readonly isLinkedToViewer: boolean;
  readonly workStatus: string;
} & Avatar_employee$key;

/**
 * Returns whether the current viewer is linked to an entity by checking
 * `isLinkedToViewer` first, then falling back to checking if
 * `currentEmployeeId` is found in `memberEmployeeIds`.
 */
export function isLinkedToViewer(
  entity: { isLinkedToViewer: boolean },
  currentEmployeeId: string | undefined,
  memberEmployeeIds: readonly string[]
): boolean {
  return (
    entity.isLinkedToViewer ||
    (!!currentEmployeeId && memberEmployeeIds.includes(currentEmployeeId))
  );
}

/**
 * Returns whether the employee is the current viewer, checking both
 * `isLinkedToViewer` and an explicit `currentEmployeeId` match.
 */
export function isViewerEmployee(
  employee: { id: string; isLinkedToViewer: boolean },
  currentEmployeeId: string | undefined
): boolean {
  return isLinkedToViewer(employee, currentEmployeeId, [employee.id]);
}

/**
 * Sorts employees so that the viewer comes first, then active (WORKING)
 * employees, then the rest. Useful for picking the most important employees
 * to display when space is limited.
 */
export function prioritizeEmployees<
  T extends { id: string; isLinkedToViewer: boolean; workStatus: string },
>(employees: readonly T[], currentEmployeeId: string | undefined): T[] {
  return [...employees].sort((a, b) => {
    const aViewer = isViewerEmployee(a, currentEmployeeId);
    const bViewer = isViewerEmployee(b, currentEmployeeId);
    if (aViewer !== bViewer) return Number(bViewer) - Number(aViewer);
    const aActive = a.workStatus === "WORKING" ? 1 : 0;
    const bActive = b.workStatus === "WORKING" ? 1 : 0;
    return bActive - aActive;
  });
}

/**
 * Sorts roles by priority:
 * 1. Linked to viewer
 * 2. Has active (WORKING) employees
 * 3. Has employees
 * 4. No employees
 */
export function prioritizeRoles<
  T extends {
    employees?: readonly {
      id: string;
      isLinkedToViewer: boolean;
      workStatus: string;
    }[];
    employeeCount?: number;
  },
>(roles: readonly (T | null)[], currentEmployeeId?: string): T[] {
  return roles.filter((r): r is T => r != null).sort((a, b) => {
    const aEmployees = a.employees ?? [];
    const bEmployees = b.employees ?? [];

    const aLinked = aEmployees.some(
      (e) => isViewerEmployee(e, currentEmployeeId)
    )
      ? 1
      : 0;
    const bLinked = bEmployees.some(
      (e) => isViewerEmployee(e, currentEmployeeId)
    )
      ? 1
      : 0;
    if (aLinked !== bLinked) return bLinked - aLinked;

    const aActive = aEmployees.some((e) => e.workStatus === "WORKING") ? 1 : 0;
    const bActive = bEmployees.some((e) => e.workStatus === "WORKING") ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;

    const aCount = a.employeeCount ?? aEmployees.length;
    const bCount = b.employeeCount ?? bEmployees.length;
    if (aCount !== bCount) return aCount > 0 ? (bCount > 0 ? 0 : -1) : 1;

    return 0;
  });
}

export function OverlappingEmployeeAvatars(props: {
  employees: readonly OverlappingEmployee[];
  maxAvatars?: number;
  highlightViewer: boolean;
  currentEmployeeId?: string;
}) {
  const { employees, maxAvatars, highlightViewer, currentEmployeeId } = props;
  const size = useCircleSize("smallMedium");

  const prioritized = prioritizeEmployees(employees, currentEmployeeId);
  const sliced =
    maxAvatars != null ? prioritized.slice(0, maxAvatars) : prioritized;
  const visible = [...sliced].reverse();

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {visible.map((employee, index) => (
        <View
          key={employee.id}
          style={{ marginLeft: index === 0 ? 0 : -size / 4 }}
        >
          <EmployeeAvatar
            employee={employee}
            size="small"
            style={
              highlightViewer && isViewerEmployee(employee, currentEmployeeId)
                ? "assigned-to-me"
                : "default"
            }
          />
        </View>
      ))}
    </View>
  );
}
